import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';

/**
 * States in which the approved total still lives in PRE_APPROVED_TOTAL rather than
 * FINAL_APPROVED_TOTAL. Mirrors the API's BEFORE_PRE_APPROVAL_STATE_STRINGS, which is
 * what `update-approved-total` uses to pick the field it writes and logs.
 */
export const BEFORE_PRE_APPROVAL_STATES = [
  'TAS.ApplicationState.CREATED',
  'TAS.ApplicationState.DEFINE_COURSES',
  'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
  'TAS.ApplicationState.COURSES_APPROVED',
  'TAS.ApplicationState.COURSES_NOT_APPROVED',
  'TAS.ApplicationState.COURSES_REJECTED',
];

/** The approved-total field a write in this state lands in. */
export function approvedTotalFieldForState(state: string) {
  return BEFORE_PRE_APPROVAL_STATES.includes(state) ? 'PRE_APPROVED_TOTAL' : 'FINAL_APPROVED_TOTAL';
}

/**
 * Allows expense edits to update approval only while its amount matches requestedTotalBeforeEdit,
 * captured before opening the editor. Deliberate approved-amount overrides are preserved.
 * Returns false after pending decisions, for Course Cash/direct pay, claims financials, vouchers,
 * or existing ledger-contributing reimbursements.
 * The API chooses payment adjustment by ledger-row existence, even for zero-value PAYABLE or
 * FORGIVEN rows; syncing those could request money instead of updating FINAL_APPROVED_TOTAL.
 * hasLedgerContributingReimbursement mirrors that scope, with amounts as an additional backstop.
 */
export function canSyncApprovedTotalFromRequest(
  application: TasApplicationModel,
  requestedTotalBeforeEdit: number | null
) {
  const template = application.tasProgramInstance?.tasProgramTemplate;

  if (!template) {
    return false;
  }

  if (
    template.isDirectPayProgram &&
    application.paymentMethod === 'TAS.AssistanceManner.COURSE_CASH'
  ) {
    return false;
  }

  if (template.calculateTotalByClaimsFinancials || template.isVoucherProgram) {
    return false;
  }

  const { state } = application;
  const isPendingDecision =
    state === 'TAS.ApplicationState.PENDING_COURSES_APPROVAL' ||
    state === 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL';

  if (!isPendingDecision) {
    return false;
  }

  // Existence, not amount — see the note above. Requires `tas-assistances` to be loaded,
  // or this reads an empty array and fails open.
  if (application.hasLedgerContributingReimbursement) {
    return false;
  }

  const amounts = application.amounts;

  if ((amounts?.paid ?? 0) !== 0 || (amounts?.pending_payment ?? 0) !== 0) {
    return false;
  }

  // No captured edit session — nothing to compare against, so make no assumptions.
  if (requestedTotalBeforeEdit === null) {
    return false;
  }

  return application.approvedTotal === requestedTotalBeforeEdit;
}

export default null; // silence a false warning
